import { FieldValues, UseFormGetValues } from 'react-hook-form'
import {
  formatDataObject,
  FormRuleType,
  getEntityDataByEntityNameIdApiForFilter,
  getGenericEntityListingAPI,
} from '@app-store'
import { parseDurationKind } from './calculateExpirationDate.utils'
import {
  ancestorFieldHasValue,
  parentFieldHasValue,
  resolveAncestorCacheRef,
  resolveAncestorFieldRef,
} from './formAncestry.utils'
import { resolveCacheProperty } from './formRuleParamResolver.utils'
import {
  FormAncestryRuntimeEntry,
  FormBehaviorFilterDef,
  FormBehaviorState,
  ListingCriterion,
} from '../types/formBehavior.types'

export const EMPTY_FORM_BEHAVIOR_STATE: FormBehaviorState = {
  requiredOverrides: {},
  disabledOverrides: {},
  lookupAllowedIds: {},
  categoryAllowedIds: {},
  minDateOverrides: {},
  maxDateOverrides: {},
}

export const resolveFieldRef = (
  getValues: UseFormGetValues<FieldValues>,
  keyName: string,
  path = 'id'
): unknown => {
  const fieldValue = getValues(keyName)
  if (fieldValue == null || fieldValue === '') return undefined
  if (path === 'id') {
    if (
      typeof fieldValue === 'object' &&
      fieldValue !== null &&
      'id' in fieldValue
    ) {
      return (fieldValue as { id: unknown }).id
    }
    return fieldValue
  }
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined
    return (acc as Record<string, unknown>)[key]
  }, fieldValue)
}

const fieldHasValue = (
  getValues: UseFormGetValues<FieldValues>,
  keyName: string,
  path = 'id'
): boolean => {
  const value = resolveFieldRef(getValues, keyName, path)
  return value != null && value !== ''
}

export const formFieldHasValue = fieldHasValue

export const normalizeListingResponse = (response: unknown): FieldValues[] => {
  if (Array.isArray(response)) return response as FieldValues[]
  if (
    response != null &&
    typeof response === 'object' &&
    Array.isArray((response as { content?: unknown }).content)
  ) {
    return (response as { content: FieldValues[] }).content
  }
  if (
    response != null &&
    typeof response === 'object' &&
    Array.isArray((response as { data?: unknown }).data)
  ) {
    return (response as { data: FieldValues[] }).data
  }
  return []
}

export const evaluateRecordId = (
  recordId: FormBehaviorFilterDef,
  getValues: UseFormGetValues<FieldValues>,
  ancestors: FormAncestryRuntimeEntry[] = []
): unknown => {
  if (recordId.from === 'field') {
    return resolveFieldRef(
      getValues,
      recordId.key_name ?? '',
      recordId.path ?? 'id'
    )
  }
  if (recordId.from === 'ancestor_field') {
    return resolveAncestorFieldRef(ancestors, {
      entity_name: recordId.entity_name,
      scope: recordId.scope,
      key_name: recordId.key_name ?? '',
      path: recordId.path,
    })
  }
}

export const buildListingCriteria = (
  filters: FormBehaviorFilterDef[] | undefined,
  getValues: UseFormGetValues<FieldValues>,
  ancestors: FormAncestryRuntimeEntry[] = []
): ListingCriterion[] | null => {
  if (!filters?.length) return []

  const criteria: ListingCriterion[] = []

  for (const filter of filters) {
    if (filter.when) {
      const hasValue = fieldHasValue(
        getValues,
        filter.when.key_name,
        filter.when.path ?? 'id'
      )
      if (filter.when.has_value !== hasValue) continue
    }

    if (filter.from === 'field') {
      const value = resolveFieldRef(
        getValues,
        filter.key_name ?? '',
        filter.path ?? 'id'
      )
      if (filter.required && (value == null || value === '')) {
        return null
      }
      if (value == null || value === '') continue
      criteria.push({
        field_name: filter.field_name,
        operation: filter.operation ?? 'EQUALS',
        values: [value],
      })
      continue
    }

    if (filter.from === 'ancestor_field') {
      const value = resolveAncestorFieldRef(ancestors, {
        entity_name: filter.entity_name,
        scope: filter.scope,
        key_name: filter.key_name ?? '',
        path: filter.path,
      })
      if (filter.required && (value == null || value === '')) {
        return null
      }
      if (value == null || value === '') continue
      criteria.push({
        field_name: filter.field_name,
        operation: filter.operation ?? 'EQUALS',
        values: [value],
      })
      continue
    }

    if (filter.operation === 'IS_NULL') {
      criteria.push({
        field_name: filter.field_name,
        operation: 'IS_NULL',
        values: [],
      })
    }
  }

  criteria.push({
    field_name: 'record_status',
    operation: 'EQUALS',
    values: ['ACTIVE'],
  })

  return criteria
}

export const fetchEntityListingForRule = async (
  rule: FormRuleType,
  getValues: UseFormGetValues<FieldValues>,
  ancestors: FormAncestryRuntimeEntry[] = []
): Promise<FieldValues[] | null> => {
  const source = rule.source as {
    entity_name?: string
    select?: string[]
    filters?: FormBehaviorFilterDef[]
  }
  if (!source?.entity_name) return []

  const criteria = buildListingCriteria(source.filters, getValues, ancestors)
  if (criteria === null) return null

  try {
    const response = await getGenericEntityListingAPI({
      entity_name: source.entity_name,
      select: source.select ?? ['id'],
      filters: [{ criteria: criteria as never }],
      page: 0,
      size: 10000,
    })
    return normalizeListingResponse(response)
  } catch (err) {
    console.error('[FormBehaviorEngine] listing fetch failed', rule.id, err)
    return []
  }
}

export const fetchRecordDataForRule = async (
  rule: FormRuleType,
  getValues: UseFormGetValues<FieldValues>,
  ancestors: FormAncestryRuntimeEntry[] = []
): Promise<Record<string, unknown>> => {
  const source = rule.source as {
    entity_name?: string
    select?: string[]
    filters?: FormBehaviorFilterDef[]
    related_entities?:
      | Record<string, { select?: string[] }>
      | Record<
          string,
          { select?: string[]; filters: { criteria: formatDataObject[] }[] }
        >
    record_id?: FormBehaviorFilterDef
  }
  if (!source?.entity_name || !source?.record_id) return {}
  const recordId = evaluateRecordId(source.record_id, getValues, ancestors)
  if (recordId == null) return {}
  const criteria = buildListingCriteria(source.filters, getValues, ancestors)
  if (criteria === null) return {}

  try {
    const response = await getEntityDataByEntityNameIdApiForFilter(
      {
        payload: {
          entity_name: source.entity_name,
          page: 0,
          size: 1,
          select: source.select ?? ['id'],
          related_entities: source.related_entities ?? {},
          filters: [{ criteria: criteria as never }],
        },
      },
      recordId.toString()
    )
    return response.data as Record<string, unknown>
  } catch (err) {
    console.error('[FormBehaviorEngine] record data fetch failed', rule.id, err)
    return {}
  }
}

export const collectWatchKeyNames = (rules: FormRuleType[]): string[] => {
  const keys = new Set<string>()
  rules.forEach((rule) => {
    const watchNames = (rule.when as { watch_key_names?: string[] })
      ?.watch_key_names
    watchNames?.forEach((name) => keys.add(name))
  })
  return Array.from(keys)
}

export const getEffectiveRequired = (
  keyName: string,
  layoutRequired: boolean | null | undefined,
  requiredOverrides: Record<string, boolean | undefined>
): boolean => {
  if (Object.prototype.hasOwnProperty.call(requiredOverrides, keyName)) {
    return requiredOverrides[keyName] ?? false
  }
  return layoutRequired ?? false
}

export const getEffectiveDisabled = (
  keyName: string,
  layoutDisabled: boolean,
  disabledOverrides: Record<string, boolean | undefined>
): boolean => {
  if (Object.prototype.hasOwnProperty.call(disabledOverrides, keyName)) {
    return disabledOverrides[keyName] ?? false
  }
  return layoutDisabled
}

export const getEffectiveDateBound = (
  keyName: string,
  layoutBound: string,
  boundOverrides: Record<string, string | undefined>
): string => {
  if (Object.prototype.hasOwnProperty.call(boundOverrides, keyName)) {
    return boundOverrides[keyName] ?? layoutBound
  }
  return layoutBound
}

export const buildFormRuleExpressionHelpers = (
  layoutUid: string,
  getValues: UseFormGetValues<FieldValues>,
  ancestors: FormAncestryRuntimeEntry[] = []
) => ({
  fieldHasValue: (keyName: string, path?: string) =>
    fieldHasValue(getValues, keyName, path ?? 'id'),
  resolveField: (keyName: string, path?: string) =>
    resolveFieldRef(getValues, keyName, path ?? 'id'),
  resolveCacheProperty: (
    cacheKey: string,
    formKeyName: string,
    property: string
  ) =>
    resolveCacheProperty(
      layoutUid,
      cacheKey,
      formKeyName,
      'id',
      property,
      getValues
    ),
  durationKindFromConsent: () =>
    parseDurationKind(
      resolveCacheProperty(
        layoutUid,
        'product_consents',
        'consent_type',
        'id',
        'duration_type',
        getValues
      )
    ) ?? '',
  ancestorFieldHasValue: (entityName: string, keyName: string, path?: string) =>
    ancestorFieldHasValue(ancestors, entityName, keyName, path ?? 'id'),
  resolveAncestorField: (entityName: string, keyName: string, path?: string) =>
    resolveAncestorFieldRef(ancestors, {
      entity_name: entityName,
      key_name: keyName,
      path: path ?? 'id',
    }),
  parentFieldHasValue: (keyName: string, path?: string) =>
    parentFieldHasValue(ancestors, keyName, path ?? 'id'),
  resolveParentField: (keyName: string, path?: string) =>
    resolveAncestorFieldRef(ancestors, {
      scope: 'parent',
      key_name: keyName,
      path: path ?? 'id',
    }),
  resolveAncestorCache: (entityName: string, cacheKey: string, path: string) =>
    resolveAncestorCacheRef(ancestors, {
      entity_name: entityName,
      cache_key: cacheKey,
      path,
    }),
})
